import { useEffect, useState } from 'react';

import { Box, DatePicker as ChakraDatePicker, HStack } from '@chakra-ui/react';
import { format } from 'date-fns';
import dayjs from 'dayjs';

import { Button } from '@m/components-ui/button';
import { CloseButton } from '@m/components-ui/close-button';
import { dateToDateValue, dateValueToDate } from '@m/components-ui/date-and-time/date-picker';
import {
  DatePickerMenuBody,
  DateTimeSection,
} from '@m/components-ui/date-and-time/date-picker-menu';
import {
  combineDateWithSelectableTime,
  getEarliestSelectableDateTime,
  getSelectableTimeOptions,
} from '@m/components-ui/date-and-time/useCalendar';
import {
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
} from '@m/components-ui/dialog';

interface ReservationDateTimeDialogProps {
  contained?: boolean;
  mode: 'start' | 'end';
  onOpenChange: (open: boolean) => void;
  onSubmit: (date: Date) => void;
  open: boolean;
  otherDate?: Date;
  value?: Date;
}

/** Default draft when the dialog opens with no value — matches the picker's pre-filled time. */
function defaultDraft(mode: 'start' | 'end', otherDate?: Date): Date {
  if (mode === 'end' && otherDate) {
    return dayjs(otherDate).add(2, 'hour').toDate();
  }
  // Walk forward when today has no remaining slots so Submit never commits a past time.
  return getEarliestSelectableDateTime(mode === 'end' ? otherDate : undefined);
}

export function ReservationDateTimeDialog({
  contained = false,
  mode,
  open,
  value,
  otherDate,
  onOpenChange,
  onSubmit,
}: ReservationDateTimeDialogProps) {
  // Tracks the in-progress selection; committed only when the user taps Submit.
  const [draft, setDraft] = useState<Date | undefined>(value);
  // The draft is seeded from "now" when empty, so it cannot double as the calendar's
  // selection — that would paint today as chosen before the user tapped a day.
  const [pickedDay, setPickedDay] = useState<Date | undefined>();
  // Bumped on each open so the picker remounts and re-seeds from `value`.
  const [openCount, setOpenCount] = useState(0);

  useEffect(() => {
    if (open) {
      // Seed a full datetime when empty so Submit is enabled with the pre-filled time.
      setDraft(value ?? defaultDraft(mode, otherDate));
      setPickedDay(undefined);
      setOpenCount((c) => c + 1);
    }
  }, [open, value, mode, otherDate]);

  const title = mode === 'start' ? 'Starts' : 'Ends';
  const minDate = mode === 'end' ? otherDate : undefined;
  // Resolve on render so Submit is never disabled for a paint while the open
  // effect catches up — time is already pre-filled in the picker.
  const resolvedDraft = open ? (draft ?? value ?? defaultDraft(mode, otherDate)) : draft;
  // Highlight only an already-chosen date or a day tapped in this session.
  const selectedDay = pickedDay ?? value;
  const timeOfDay = resolvedDraft ? format(resolvedDraft, 'HH:mm') : '12:00';
  const timeOptions = getSelectableTimeOptions(resolvedDraft, minDate);

  return (
    <DialogRoot
      open={open}
      onOpenChange={(e) => onOpenChange(e.open)}
      size="full"
      scrollBehavior="inside"
      closeOnInteractOutside={false}
    >
      {/* Clamp to the dynamic viewport height and lay out as a column so the tall
          calendar body scrolls on its own and the Submit footer stays pinned and
          visible (otherwise it's pushed below the fold on short mobile screens). */}
      <DialogContent
        display="flex"
        flexDirection="column"
        h="100dvh"
        maxH="100dvh"
        portalled={!contained}
        backdrop={!contained}
      >
        <DialogHeader flexShrink={0} py="4">
          <HStack justify="space-between" align="center" w="full">
            <Box w="32px" />
            <DialogTitle flex={1} textAlign="center">
              {title}
            </DialogTitle>
            <CloseButton size="sm" onClick={() => onOpenChange(false)} />
          </HStack>
        </DialogHeader>

        <DialogBody flex="1" minH={0} overflowY="auto" p={4}>
          <ChakraDatePicker.Root
            key={openCount}
            inline
            hideOutsideDays
            size="md"
            startOfWeek={0}
            selectionMode="single"
            value={selectedDay ? [dateToDateValue(selectedDay)] : []}
            // Open on the seeded month even when nothing is selected.
            defaultFocusedValue={resolvedDraft ? dateToDateValue(resolvedDraft) : undefined}
            min={minDate ? dateToDateValue(minDate) : dateToDateValue(new Date())}
            onValueChange={({ value: nextValue }) => {
              const day = nextValue[0];
              if (!day) return;
              const combined = combineDateWithSelectableTime(
                dateValueToDate(day, timeOfDay),
                timeOfDay,
                minDate,
              );
              if (!combined) return;
              setDraft(combined);
              setPickedDay(combined);
            }}
          >
            <ChakraDatePicker.Content unstyled>
              <DatePickerMenuBody type="datetime" selectionMode="single" />
              <DateTimeSection
                value={resolvedDraft ? [dateToDateValue(resolvedDraft)] : []}
                startTime={timeOfDay}
                startTimeOptions={timeOptions}
                selectionMode="single"
                px="0"
                onStartTimeChange={(time) => {
                  if (!resolvedDraft) return;
                  setDraft(dateValueToDate(dateToDateValue(resolvedDraft), time));
                }}
              />
            </ChakraDatePicker.Content>
          </ChakraDatePicker.Root>
        </DialogBody>

        <DialogFooter flexShrink={0} px={4} pb={4}>
          <Button
            w="full"
            disabled={!resolvedDraft}
            onClick={() => {
              if (resolvedDraft) onSubmit(resolvedDraft);
            }}
          >
            Submit
          </Button>
        </DialogFooter>
      </DialogContent>
    </DialogRoot>
  );
}
